'use client';

import React, { forwardRef } from 'react';
import type { CollectionPulse } from '@/hooks/useCollectionAnalysis';
import type { AbstractFearGreed } from '@/hooks/useMarketAnalysis';

interface NFTHighlight {
  name: string;  // slug
  displayName?: string;  // proper display name from config
  floorPrice: number;
  floorSymbol: string;
  oneDayChange?: number;
  volume24h?: number;  // MUST be oneDayVolume only, NOT totalVolume
  imageUrl?: string | null;  // Collection image
}

interface TvlDataPoint {
  date: string;
  tvl: number;
}

interface NFTShareCardProps {
  collectionPulse?: CollectionPulse | null;
  nftHighlights: NFTHighlight[];
  nftCount: number;
  totalVolume24h?: number;
  abstractFearGreed?: AbstractFearGreed | null;
  historicalTvl?: TvlDataPoint[];
  currentTvl?: number;
  tvlChange24h?: number;
}

/**
 * Mini TVL sparkline chart for share card
 */
function MiniTvlChart({ data, isPositive }: { data: TvlDataPoint[]; isPositive: boolean }) {
  if (!data || data.length < 2) return null;

  const width = 200;
  const height = 50;
  const padding = 4;

  const tvlValues = data.map(d => d.tvl);
  const minTvl = Math.min(...tvlValues);
  const maxTvl = Math.max(...tvlValues);
  const range = maxTvl - minTvl || 1;

  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const points = data.map((point, index) => {
    const x = padding + (index / (data.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((point.tvl - minTvl) / range) * chartHeight;
    return { x, y };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ');

  const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(1)},${height - padding} L ${padding},${height - padding} Z`;

  const strokeColor = isPositive ? '#84cc16' : '#ef4444';
  const gradientId = 'nft-tvl-gradient';

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={strokeColor} stopOpacity="0.3" />
          <stop offset="100%" stopColor={strokeColor} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path
        d={linePath}
        fill="none"
        stroke={strokeColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Format TVL for display
 */
function formatTvlNumber(num: number): string {
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

/**
 * Get Fear/Greed color config
 */
function getFearGreedConfig(value: number) {
  if (value <= 25) return { color: '#14b8a6', label: 'Extreme Fear' };
  if (value <= 45) return { color: '#10b981', label: 'Fear' };
  if (value <= 55) return { color: '#eab308', label: 'Neutral' };
  if (value <= 75) return { color: '#f97316', label: 'Greed' };
  return { color: '#ef4444', label: 'Extreme Greed' };
}

/**
 * Mini semi-circle gauge for share card
 */
function MiniGauge({ value }: { value: number }) {
  const config = getFearGreedConfig(value);
  const needleRotation = -90 + (value / 100) * 180;
  
  const size = 48;
  const strokeWidth = 5;
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = size / 2;

  return (
    <div style={{ position: 'relative', width: size, height: size / 2 + 6 }}>
      <svg 
        width={size} 
        height={size / 2 + 6}
        viewBox={`0 0 ${size} ${size / 2 + 6}`}
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="nft-share-gauge-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#14b8a6" />
            <stop offset="25%" stopColor="#10b981" />
            <stop offset="50%" stopColor="#eab308" />
            <stop offset="75%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
        </defs>
        
        {/* Background arc */}
        <path
          d={`M ${strokeWidth / 2} ${centerY} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${centerY}`}
          fill="none"
          stroke="#1a1a1a"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Colored gradient arc */}
        <path
          d={`M ${strokeWidth / 2} ${centerY} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${centerY}`}
          fill="none"
          stroke="url(#nft-share-gauge-grad)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Needle */}
        <g transform={`rotate(${needleRotation} ${centerX} ${centerY})`}>
          <line
            x1={centerX}
            y1={centerY - 3}
            x2={centerX}
            y2={centerY - radius * 0.65}
            stroke={config.color}
            strokeWidth={2}
            strokeLinecap="round"
          />
        </g>
        
        {/* Center dot */}
        <circle cx={centerX} cy={centerY} r={3} fill={config.color} />
      </svg>
    </div>
  );
}

/**
 * Format slug to display name (capitalize and replace hyphens)
 */
function formatCollectionName(slug: string): string {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format volume with proper units
 */
function formatVolume(num: number, symbol: string = 'ETH'): string {
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K ${symbol}`;
  if (num >= 1) return `${num.toFixed(2)} ${symbol}`;
  if (num >= 0.01) return `${num.toFixed(3)} ${symbol}`;
  return `${num.toFixed(4)} ${symbol}`;
}

/**
 * Format floor price
 */
function formatFloorPrice(price: number, symbol: string = 'ETH'): string {
  if (price >= 1) return `${price.toFixed(2)} ${symbol}`;
  if (price >= 0.01) return `${price.toFixed(3)} ${symbol}`;
  return `${price.toFixed(4)} ${symbol}`;
}

/**
 * NFT Market Share Card
 * Clean NFT-focused layout with collections ranked by 24H volume
 * 
 * IMPORTANT: Uses ONLY oneDayVolume, never falls back to totalVolume (all-time)
 */
const NFTShareCard = forwardRef<HTMLDivElement, NFTShareCardProps>(
  ({ collectionPulse, nftHighlights, nftCount, totalVolume24h, abstractFearGreed, historicalTvl, currentTvl, tvlChange24h }, ref) => {
    
    const timestamp = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    // Get AI insight from collection pulse
    const nftInsight = collectionPulse?.summary;

    // Calculate total 24H volume from highlights if not provided
    const calculatedVolume24h = totalVolume24h ?? nftHighlights.reduce((sum, h) => sum + (h.volume24h || 0), 0);

    // Display top 3 collections
    const displayCollections = nftHighlights.slice(0, 3);

    // Green/teal glow for NFT market (Abstract brand consistency)
    const glowConfig = {
      bgGlow: 'radial-gradient(ellipse at top right, rgba(20, 184, 166, 0.12) 0%, transparent 50%)',
      accentGradient: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)',
      accentColor: '#14b8a6',
    };

    return (
      <div
        ref={ref}
        style={{
          width: '480px',
          maxWidth: '100%',
          minWidth: '360px',
          padding: '24px',
          background: 'linear-gradient(180deg, #0a0a0a 0%, #171717 100%)',
          borderRadius: '16px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          position: 'relative',
          boxSizing: 'border-box',
          border: '1px solid #262626',
          overflow: 'hidden',
        }}
      >
        {/* Background glow effect */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: glowConfig.bgGlow,
            pointerEvents: 'none',
          }}
        />

        {/* Decorative corner accent */}
        <div
          style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '200px',
            height: '200px',
            background: glowConfig.accentGradient,
            opacity: 0.1,
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}
        />
        {/* === HEADER === */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
            position: 'relative',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src="/Abstract_AppIcon_DarkMode.webp"
              alt="Abstract"
              style={{ width: '32px', height: '32px', borderRadius: '8px' }}
            />
            <div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>
                Abstract NFT Market
              </div>
              <div style={{ fontSize: '11px', color: '#525252', marginTop: '2px' }}>
                {timestamp}
              </div>
            </div>
          </div>
        </div>

        {/* === KEY STATS === */}
        <div 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            background: '#111',
            borderRadius: '12px',
            marginBottom: '16px',
            position: 'relative',
          }}
        >
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', marginBottom: '6px' }}>
              Collections
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#fff' }}>{nftCount}</div>
          </div>
          <div style={{ width: '1px', height: '40px', background: '#262626', flexShrink: 0 }} />
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', marginBottom: '6px' }}>
              24H Volume
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#14b8a6' }}>
              {formatVolume(calculatedVolume24h, 'ETH')}
            </div>
          </div>
        </div>

        {/* === ABSTRACT FEAR & GREED === */}
        {abstractFearGreed && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              background: '#111',
              borderRadius: '10px',
              marginBottom: '16px',
            }}
          >
            <MiniGauge value={abstractFearGreed.value} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', marginBottom: '2px' }}>
                Abstract Fear & Greed
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span style={{ fontSize: '20px', fontWeight: 700, color: getFearGreedConfig(abstractFearGreed.value).color, fontFamily: 'monospace' }}>
                  {abstractFearGreed.value}
                </span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: getFearGreedConfig(abstractFearGreed.value).color }}>
                  {abstractFearGreed.classification}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* === ABSTRACT TVL CHART === */}
        {historicalTvl && historicalTvl.length > 2 && (
          <div
            style={{
              padding: '12px 16px',
              background: '#111',
              borderRadius: '10px',
              marginBottom: '16px',
              position: 'relative',
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', fontWeight: 600 }}>
                Abstract TVL (30D)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {currentTvl && (
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>
                    {formatTvlNumber(currentTvl)}
                  </span>
                )}
                {tvlChange24h !== undefined && (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: tvlChange24h >= 0 ? '#84cc16' : '#ef4444'
                  }}>
                    {tvlChange24h >= 0 ? '+' : ''}{tvlChange24h.toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <MiniTvlChart
                data={historicalTvl.slice(-30)}
                isPositive={historicalTvl[historicalTvl.length - 1]?.tvl >= historicalTvl[0]?.tvl}
              />
            </div>
            <div style={{ fontSize: '9px', color: '#404040', textAlign: 'right', marginTop: '4px' }}>
              DeFiLlama
            </div>
          </div>
        )}

        {/* === TOP COLLECTIONS BY 24H VOLUME === */}
        <div style={{ marginBottom: '16px', position: 'relative' }}>
          <div style={{ 
            fontSize: '11px', 
            color: '#737373', 
            textTransform: 'uppercase', 
            fontWeight: 600,
            marginBottom: '12px' 
          }}>
            TOP COLLECTIONS BY 24H VOLUME
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {displayCollections.map((collection, idx) => (
              <div 
                key={collection.name}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  padding: '12px 16px',
                  background: '#111',
                  borderRadius: '10px',
                  gap: '12px',
                }}
              >
                {/* Rank Badge */}
                <div 
                  style={{ 
                    position: 'relative',
                    width: '40px', 
                    height: '40px', 
                    flexShrink: 0,
                  }}
                >
                  {/* Collection Image */}
                  {collection.imageUrl ? (
                    <img 
                      src={collection.imageUrl}
                      alt={collection.name}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <div 
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: '#262626',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        color: '#525252',
                      }}
                    >
                      🖼️
                    </div>
                  )}
                  {/* Rank Badge Overlay */}
                  <div 
                    style={{ 
                      position: 'absolute',
                      bottom: '-4px',
                      right: '-4px',
                      width: '18px', 
                      height: '18px', 
                      borderRadius: '4px',
                      background: idx === 0 ? '#14b8a6' : idx === 1 ? '#737373' : '#525252',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#fff',
                      border: '2px solid #111',
                    }}
                  >
                    {idx + 1}
                  </div>
                </div>
                
                {/* Collection Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#fff', marginBottom: '2px' }}>
                    {collection.displayName || formatCollectionName(collection.name)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#737373' }}>
                    Floor: {formatFloorPrice(collection.floorPrice, collection.floorSymbol)}
                  </div>
                </div>
                
                {/* Volume */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#14b8a6' }}>
                    {formatVolume(collection.volume24h || 0, collection.floorSymbol)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#525252' }}>24H Vol</div>
                </div>
              </div>
            ))}
            
            {displayCollections.length === 0 && (
              <div 
                style={{ 
                  padding: '20px',
                  background: '#111',
                  borderRadius: '10px',
                  textAlign: 'center',
                  color: '#525252',
                  fontSize: '12px',
                }}
              >
                No 24H volume data available
              </div>
            )}
          </div>
        </div>

        {/* === AI INSIGHT === */}
        {nftInsight && (
          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <div style={{ fontSize: '10px', color: '#737373', textTransform: 'uppercase', marginBottom: '8px', fontWeight: 600 }}>
              AI MARKET INSIGHT
            </div>
            <div 
              style={{ 
                fontSize: '12px', 
                color: '#a3a3a3', 
                lineHeight: 1.6,
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                padding: '12px',
                background: '#111',
                borderRadius: '8px',
              }}
            >
              {nftInsight}
            </div>
          </div>
        )}

        {/* === FOOTER === */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: '14px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
          }}
        >
          <div style={{ fontSize: '9px', color: '#525252', letterSpacing: '0.04em' }}>
            Data: OpenSea
          </div>
          {/* White wordmark only (descout-logo-light is black → invisible on dark) */}
          <img
            src="/descout-wordmark-white.svg"
            alt="Descout"
            style={{ height: '16px', width: 'auto' }}
          />
        </div>
      </div>
    );
  }
);

NFTShareCard.displayName = 'NFTShareCard';

export default NFTShareCard;
